import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { TripType } from "@b2bportal/air-shopping-api";
import { FlightShopSelectors } from "@b2bportal/core-flights";
import type { NavigationAction } from "@b2bportal/core-types";

type useReviewPageContainerProps = NavigationAction & {
  onError: () => void;
  goToNextStep: () => void;
};

export const useReviewPageContainer = ({
  goToNextStep,
  onError,
}: useReviewPageContainerProps) => {
  const { tripDetails, fareDetails } = useSelector(
    FlightShopSelectors.getSelectedTripDetailsAndFareDetails
  );
  const airports = useSelector(FlightShopSelectors.getAirports);

  const paxPricing = useSelector(FlightShopSelectors.getSelectedFarePaxPricing);

  const isOutgoingMultiTicket = useSelector(
    FlightShopSelectors.isOutgoingMultiTicket
  );
  const isReturnMultiTicket = useSelector(
    FlightShopSelectors.isReturnMultiTicket
  );
  const isMultiTicket = isOutgoingMultiTicket || isReturnMultiTicket;

  const isHackerFare = useSelector(FlightShopSelectors.isHackerFare);

  const selectedMulticityTrips = useSelector(
    FlightShopSelectors.getSelectedMulticityTrips
  );

  const tripType = useMemo(() => {
    if (
      selectedMulticityTrips?.every((trip) => trip.tripId != null) &&
      selectedMulticityTrips?.length > 1
    ) {
      return TripType.open_jaw;
    } else if (tripDetails.slices.length > 1) {
      return TripType.round_trip;
    } else {
      return TripType.one_way;
    }
  }, [selectedMulticityTrips, tripDetails]);

  React.useEffect(() => {
    if (tripDetails == null || fareDetails == null || paxPricing == null) {
      onError();
    }
  }, [paxPricing, tripDetails, fareDetails, onError]);

  return React.useMemo(
    () => ({
      context: {
        tripDetails,
        fareDetails,
        tripType,
        airports,
        paxPricing,
        isMultiTicket,
        isHackerFare,
      },
      handlers: { goToNextStep },
    }),
    [
      airports,
      fareDetails,
      tripType,
      goToNextStep,
      isHackerFare,
      isMultiTicket,
      paxPricing,
      tripDetails,
    ]
  );
};
